// runRedesignScene — executor for the agent's gated `redesignScene` mutation, called by
// BulkMutateToolUI on the author's "Redesign" click (never auto-applied).
//
//   POST {MW_API_URL}api/course-build/:trainingId/scene/:navId/redesign { format, variant } → { jobId }
//   409 build running · 402 insufficient credits · 400 { error: 'redesign_unsupported' } (tekno1)
//   · 413 { error: 'scene_too_large' }
//
// mw rebuilds from the SAVED training, so unsaved edits are flushed first (failure aborts, no
// POST). The build lock (saves refused, autosave paused, structural edits ignored) is held from
// then until the re-import lands, so nothing is saved over the server copy.
//
// mw rebuilds the scene server-side (replaceAgentScene into the training's exportedState). When
// the job completes this re-imports the training — the same importStateAsync build mode uses —
// so the open editor shows it. That import clears undo history (the server copy is the truth).

import { authedFetch } from "../../../common/http_service";
import { waitForJob } from "../../../common/job_poll.es6";
import { importStateAsync, selectNavItem } from "../../../common/actions.es6";
import { setBaseUpdatedAt } from "../../../common/saveConflict.es6";
import { isBuildLocked, setBuildLocked } from "./buildMode";
import { notifyInsufficientBalance } from "../../utils/insufficientBalance";

/**
 * @returns {Promise<{ ok: true } | { error: string }>}
 *   error: "scene-missing" | "no-training" | "busy" | "unsupported" | "insufficient-balance"
 *   | "timeout" | "save-failed" | "too-large" | "failed" (server text is never relayed).
 */
export async function runRedesignScene(dispatch, getState, params, scope) {
  const navId = scope && scope.navId;
  const format = params && params.format;
  const state = typeof getState === "function" ? getState() : null;
  const present = state && state.undoGroup && state.undoGroup.present;
  if (!navId || !format || !present || !(present.navItemsById || {})[navId]) {
    return { error: "scene-missing" };
  }
  const trainingId = window.idealStudioTrainingId;
  if (!trainingId) return { error: "no-training" };
  const saved = await new Promise((resolve) => {
    try {
      window.ideaEnvSaveState(
        state,
        false,
        () => resolve(true),
        () => resolve(false)
      );
    } catch {
      resolve(false);
    }
  });
  if (!saved) return { error: "save-failed" };
  const wasLocked = isBuildLocked();
  setBuildLocked(true);
  try {
    return await redesignAndReimport(dispatch, getState, trainingId, navId, format, params);
  } finally {
    setBuildLocked(wasLocked);
  }
}

async function redesignAndReimport(dispatch, getState, trainingId, navId, format, params) {
  let jobId;
  try {
    const response = await authedFetch(
      `${process.env.MW_API_URL}api/course-build/${encodeURIComponent(trainingId)}/scene/${encodeURIComponent(navId)}/redesign`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ format, variant: Number(params.variant) || 1 }),
      },
      { quiet: true }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status === 409) return { error: "busy" };
      if (response.status === 402) {
        const details = data.details || {};
        notifyInsufficientBalance({
          requestedAmount: details.requiredCredits ?? "",
          currentBalance: details.availableCredits ?? "",
        });
        return { error: "insufficient-balance" };
      }
      if (response.status === 413) return { error: "too-large" };
      return { error: data.error === "redesign_unsupported" ? "unsupported" : "failed" };
    }
    jobId = data.jobId;
    if (!jobId) return { error: "failed" };
    await waitForJob(jobId);
  } catch (err) {
    return { error: err && err.message === "timeout" ? "timeout" : "failed" };
  }
  const keep = getState().undoGroup.present.navItemSelected;
  await dispatch(
    importStateAsync(trainingId, window.tenantId, (r) => setBaseUpdatedAt(r.updatedAt))
  );
  if (((getState().undoGroup.present || {}).navItemsById || {})[keep])
    dispatch(selectNavItem(keep));
  return { ok: true };
}
